"use client";

import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm, FormProvider } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { 
  MultiStepFormProps, 
  StepProps, 
  BaseFormData
} from './types';
import { ThankYouPage, ThankYouPageConfig } from './ThankYouPage';
import { FormErrorBoundary, useFormErrorHandler } from './FormErrorBoundary';
import { useReCaptchaV3Execute } from './ReCaptchaV3';
import { ReCaptchaV3Provider } from './ReCaptchaV3Provider';
import Link from 'next/link';
import { motion, AnimatePresence } from '@/components/LazyMotion';

function MultiStepFormInner<T extends object>({ config, className }: MultiStepFormProps<T>) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { handleError } = useFormErrorHandler();
  
  const urlStep = searchParams.get('step');
  const initialStep = urlStep ? parseInt(urlStep) : 1;
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [, setSubmitAttempts] = useState(0);//not used for now
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [, setRecaptchaScore] = useState<number | null>(null);//not used for now
  // Special thank you page handling for "no" selections
  const [showNoSelectionThankYou, setShowNoSelectionThankYou] = useState(false);
  const [noSelectionThankYouConfig, setNoSelectionThankYouConfig] = useState<ThankYouPageConfig | null>(null);
  
  // Animation direction for step transitions
  const [animationDirection, setAnimationDirection] = useState<'forward' | 'backward'>('forward');

  const { execute: executeRecaptcha, ready: recaptchaReady } = useReCaptchaV3Execute();
  
  const form = useForm({
    defaultValues: config.initialData as unknown as Record<string, unknown>,
    mode: 'onChange',
    shouldUnregister: false,
  });

  // Single source of truth for data (reactive)
  const formData = form.watch() as T;
  
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;
  
  const formRef = useRef<HTMLFormElement>(null);

  // Helper function to reset focus to the first focusable element in the form
  const resetFocus = useCallback(() => {
    // Use requestAnimationFrame to ensure DOM updates are complete
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (formRef.current) {
          // Find the first focusable element within the current step content
          const stepContainer = formRef.current.querySelector('[data-step-content]') || formRef.current;
          const focusableElements = stepContainer.querySelectorAll(
            'input:not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          
          if (focusableElements.length > 0) {
            // Focus the first focusable element
            (focusableElements[0] as HTMLElement).focus();
          } else {
            // Fallback to form element if no focusable elements found
            formRef.current.focus();
          }
        }
      }, 100); // Small delay to ensure step content is rendered
    });
  }, []);

  // Filter steps based on shouldShow conditions
  const visibleSteps = useMemo(() => {
    return config.steps.filter(step => {
      if (!step.shouldShow) return true; // Show step if no shouldShow condition
      return step.shouldShow(formData);
    });
  }, [config.steps, formData]);

  const totalSteps = visibleSteps.length;
  
  // Submit button configuration
  const submitButtonConfig = useMemo(() => config.submitButton || {
    text: 'Submit',
    loadingText: 'Submitting...',
    showOnStep: 'all', // Always show by default
    size: 'lg',
    variant: 'primary',
    className: 'button-branded'
  }, [config.submitButton]);

  // reCAPTCHA configuration logic
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;
  
  // reCAPTCHA v3 configuration - simplified since server-side verification handles score checking
  const recaptchaConfig = useMemo(() => {
    return {
      enabled: config.requireRecaptcha === true
    };
  }, [config.requireRecaptcha]);

  useEffect(() => {
    config.onStepChange?.(currentStep, totalSteps);
    
    // Reset reCAPTCHA token when navigating away from final step
    if (currentStep !== totalSteps && recaptchaToken) {
      setRecaptchaToken(null);
      setRecaptchaScore(null);
    }
  }, [currentStep, totalSteps, config, recaptchaToken]);

  // Separate effect for scrolling that only triggers on actual step navigation
  useEffect(() => {
    // Only scroll to top when actually changing steps (not when reCAPTCHA state changes)
    if (currentStep > 1) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    }
  }, [currentStep]); // Only depend on currentStep, not other state changes

  // Rate limiting for form submissions
  const MAX_SUBMIT_ATTEMPTS = 3;
  const RATE_LIMIT_WINDOW = 60000; // 1 minute
  const lastSubmitTime = useRef<number>(0);
  const submitAttemptsRef = useRef<number>(0);

  
  const canSubmit = useCallback(() => {
    const now = Date.now();
    const timeSinceLastSubmit = now - lastSubmitTime.current;
    
    // Reset attempts if enough time has passed
    if (timeSinceLastSubmit > RATE_LIMIT_WINDOW) {
      submitAttemptsRef.current = 0;
    }
    
    return submitAttemptsRef.current < MAX_SUBMIT_ATTEMPTS;
  }, []);

  const areAllPreviousStepsValid = useCallback((targetStep: number) => {
    for (let i = 0; i < targetStep - 1; i++) {
      const stepData = visibleSteps[i];
      if (stepData.validation && !stepData.validation(form.getValues() as T)) {
        return false;
      }
    }
    return true;
  }, [visibleSteps, form]);

  const canNavigateToStep = useCallback((step: number) => {
    if (step < currentStep) return true;
    if (step > currentStep + 1) return false;
    if (step === currentStep + 1) {
      const currentStepData = visibleSteps[currentStep - 1];
      if (!currentStepData?.validation) return true;
      return currentStepData.validation(form.getValues() as T);
    }
    return true;
  }, [currentStep, visibleSteps, form]);

  const goToStep = useCallback((step: number) => {
    if (step === 999) {
      setShowThankYou(true);
      return;
    }
    if (step >= 1 && step <= totalSteps && canNavigateToStep(step)) {
      const direction = step > currentStep ? 'forward' : 'backward';
      setAnimationDirection(direction);
      
      const url = new URL(window.location.href);
      url.searchParams.set('step', step.toString());
      router.push(url.pathname + url.search, { scroll: false });
      setCurrentStep(step);
      
      // Reset focus to the top of the form when navigating to next step
      resetFocus();
      
      // Reset no-selection thank you state when navigating to any step
      if (showNoSelectionThankYou) {
        setShowNoSelectionThankYou(false);
        setNoSelectionThankYouConfig(null);
      }
    }
  }, [totalSteps, canNavigateToStep, router, showNoSelectionThankYou, currentStep, resetFocus]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      const prev = currentStep - 1;
      setAnimationDirection('backward');
      
      const url = new URL(window.location.href);
      url.searchParams.set('step', prev.toString());
      router.push(url.pathname + url.search, { scroll: false });
      setCurrentStep(prev);
      
      // Reset focus to the top of the form when navigating to previous step
      resetFocus();
      
      // Reset no-selection thank you state when going back
      if (showNoSelectionThankYou) {
        setShowNoSelectionThankYou(false);
        setNoSelectionThankYouConfig(null);
      }
    }
  }, [currentStep, router, showNoSelectionThankYou, resetFocus]);

  // Execute reCAPTCHA v3 and verify server-side (10s timeout, 1 retry on failure)
  const executeRecaptchaV3 = useCallback(async (): Promise<{ token: string; score?: number } | null> => {
    if (!recaptchaReady || !executeRecaptcha) {
      console.warn('reCAPTCHA v3 not ready');
      return null;
    }

    const RECAPTCHA_TIMEOUT_MS = 10000;

    const attempt = async (): Promise<{ token: string; score?: number }> => {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('reCAPTCHA timeout after 10 seconds')), RECAPTCHA_TIMEOUT_MS)
      );
      const token = await Promise.race([executeRecaptcha('form_submit'), timeoutPromise]) as string;
      if (!token) throw new Error('No token');
      setRecaptchaToken(token);
      return { token };
    };

    for (let i = 0; i < 2; i++) {
      try {
        return await attempt();
      } catch (error) {
        if (i === 1) {
          console.error('reCAPTCHA v3 execution failed:', error);
          setSubmitError('Security verification failed. Please try again.');
          return null;
        }
      }
    }
    return null;
  }, [executeRecaptcha, recaptchaReady]);

  // Check if current step is valid (reactive to form data changes)
  const isCurrentStepValid = useMemo(() => {
    const currentStepData = visibleSteps[currentStep - 1];
    if (!currentStepData?.validation) return true;
    return currentStepData.validation(formData);
  }, [visibleSteps, currentStep, formData]);

  const handleFormSubmit = useCallback(async () => {
    // If not on last step, check for special step behaviors
    if (!isLastStep) {
      const currentStepData = visibleSteps[currentStep - 1];
      
      // Validate current step
      if (currentStepData?.validation && !currentStepData.validation(form.getValues() as T)) {
        setSubmitError('Please complete all required fields before continuing.');
        return;
      }

      // Check for special "no" selection handling
      if (currentStepData?.noSelectionRedirectToThankYou) {
        const formData = form.getValues() as T;
        
        // Try to find the selection value from stepConfig or form data
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let selectedValue: any = null;
        
        if (currentStepData.stepConfig?.fieldName) {
          // Use the field name specified in stepConfig
          const fieldName = currentStepData.stepConfig.fieldName;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          selectedValue = (formData as any)[fieldName];
        } else {
          // Fallback: look for fields containing 'Selection' or 'selection'
          for (const key of Object.keys(formData)) {
            if (key.includes('Selection') || key.includes('selection')) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              selectedValue = (formData as any)[key];
              break;
            }
          }
        }
        
        // Check if selection is "no" and should redirect to thank you
        if (selectedValue === currentStepData.noSelectionRedirectToThankValue &&  
            currentStepData.showThankYouWithoutSubmit && 
            currentStepData.noSelectionThankYouConfig) {
          // Show custom thank you page without submitting
          setNoSelectionThankYouConfig(currentStepData.noSelectionThankYouConfig);
          setShowNoSelectionThankYou(true);
          return;
        }
      }
      
      // Normal flow - go to next step
      goToStep(currentStep + 1);
      return;
    }

    // Last step - handle actual form submission
    if (!config.onSubmit) return;
    
    // Rate limiting check
    if (!canSubmit()) {
      setSubmitError('Too many submission attempts. Please wait a moment before trying again.');
      return;
    }

    // Execute reCAPTCHA v3 if enabled
    let recaptchaResult: { token: string; score?: number } | null = null;
    if (recaptchaConfig.enabled) {
      recaptchaResult = await executeRecaptchaV3();
      
      if (!recaptchaResult) {
        setSubmitError('Security verification failed. Please try again.');
        return;
      }
    }


    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Update rate limiting counters
      lastSubmitTime.current = Date.now();
      submitAttemptsRef.current += 1;
      setSubmitAttempts(prev => prev + 1);
      
      // Include reCAPTCHA token and score in form data if used
      const formValues = form.getValues();
      const formDataForSubmission = recaptchaResult ? {
        ...formValues,
        recaptchaToken: recaptchaResult.token,
        ...(recaptchaResult.score !== undefined && { recaptchaScore: recaptchaResult.score })
      } : formValues;
      
      await config.onSubmit(formDataForSubmission as T);
      
      // Reset error state on successful submission
      setSubmitError(null);
      setSubmitAttempts(0);
      submitAttemptsRef.current = 0;
      setRecaptchaToken(null);
      setShowThankYou(true);
      
    } catch (error) {
      // const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      
      // Reset reCAPTCHA on submission error
      setRecaptchaToken(null);
      setRecaptchaScore(null);
      
      setSubmitError('Having trouble? You can contact support or try refreshing the page.');
      // Set user-friendly error message
      // if (errorMessage.includes('recaptcha') || errorMessage.includes('captcha')) {
      //   setSubmitError('reCAPTCHA verification failed. Please complete the verification and try again.');
      // } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      //   setSubmitError('Network error. Please check your connection and try again.');
      // } else if (errorMessage.includes('timeout')) {
      //   setSubmitError('Request timed out. Please try again.');
      // } else if (errorMessage.includes('validation')) {
      //   setSubmitError('Please check your form data and try again.');
      // } else {
      //   setSubmitError('Submission failed. Please try again or contact support if the problem persists.');
      // }
      
      // Log error for debugging
      handleError(error instanceof Error ? error : new Error(String(error)), 'form submission');
      
    } finally {
      setIsSubmitting(false);
    }
  }, [config, form, canSubmit, handleError, isLastStep, goToStep, currentStep, executeRecaptchaV3, recaptchaConfig, visibleSteps]);

  useEffect(() => {
    if (urlStep) {
      const targetStep = parseInt(urlStep);
      
      // Check if step number is valid (within range of visible steps)
      if (targetStep < 1 || targetStep > totalSteps || isNaN(targetStep)) {
        setCurrentStep(1);
        const url = new URL(window.location.href);
        url.searchParams.set('step', '1');
        router.replace(url.pathname + url.search, { scroll: false });
        
        // Reset focus to the top of the form when redirecting to step 1
        resetFocus();
        return;
      }
      
      // Check if previous steps are valid for steps > 1
      if (targetStep > 1 && !areAllPreviousStepsValid(targetStep)) {
        setCurrentStep(1);
        const url = new URL(window.location.href);
        url.searchParams.set('step', '1');
        router.replace(url.pathname + url.search, { scroll: false });
        
        // Reset focus to the top of the form when redirecting to step 1 due to validation
        resetFocus();
        return;
      }
      
      if (targetStep !== currentStepRef.current) {
        setCurrentStep(targetStep);
        
        // Reset focus to the top of the form when navigating via URL
        resetFocus();
      }
    }
  }, [urlStep, areAllPreviousStepsValid, router, totalSteps, resetFocus]);

  const currentStepData = visibleSteps[currentStep - 1];

  // If currentStepData is undefined, don't render the form yet
  // This can happen during step validation/redirect
  if (!currentStepData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Process step description - handle both string and function types
  let processedDescription: string | undefined;
  if (typeof currentStepData.description === 'function') {
    // If description is a function, call it with formData
    processedDescription = currentStepData.description(formData);
  } else if (typeof currentStepData.description === 'string') {
    // If description is a string, replace template variables
    processedDescription = currentStepData.description.replace(
      /\{\{businessName\}\}/g, 
      (formData as unknown as BaseFormData).businessName as string || ''
    ).replace(
      /\{\{firstName\}\}/g,
      (formData as unknown as BaseFormData).firstName as string || ''
    );
  }

  const stepProps: StepProps<T> = {
    formData,
    goToStep,
    goToPreviousStep,
    currentStep,
    totalSteps,
    title: currentStepData.title,
    description: processedDescription,
    onSubmit: handleFormSubmit,
    validation: currentStepData.validation,
    isSubmitting,
    formOptions: currentStepData.formOptions || config.formOptions,
    onError: handleError,
    isLastStep, // Pass this so individual steps can hide their buttons
    ...(currentStepData.stepConfig && { stepConfig: currentStepData.stepConfig }),
    ...Object.fromEntries(
      Object.entries(currentStepData).filter(([key]) => 
        !['id', 'title', 'description', 'component', 'validation', 'isOptional', 'formOptions', 'stepConfig'].includes(key)
      )
    ),
  };

  const progressPercentage = (currentStep / totalSteps) * 90;

  // Show no-selection thank you page if configured
  if (showNoSelectionThankYou && noSelectionThankYouConfig) {
    const processedConfig = {
      ...noSelectionThankYouConfig,
      title: noSelectionThankYouConfig.title,
      subtitle: noSelectionThankYouConfig.subtitle,
      businessName: noSelectionThankYouConfig.businessName?.replace('{{businessName}}', (formData as unknown as BaseFormData).businessName as string || ''),
      firstName: noSelectionThankYouConfig.firstName?.replace('{{firstName}}', (formData as unknown as BaseFormData).firstName as string || ''),
      nextStepsItems: noSelectionThankYouConfig.nextStepsItems,
      ctaButton: noSelectionThankYouConfig.ctaButton,
      additionalInfo: noSelectionThankYouConfig.additionalInfo,
      socialButton: noSelectionThankYouConfig.socialButton
    };
    return (
      <FormProvider {...form}>
        <ThankYouPage 
          config={processedConfig} 
          className={className}
          brandColor={config.brandColor}
          brandColorHover={config.brandColorHover}
          brandColorText={config.brandColorText}
          brandColorLink={config.brandColorLink}
          brandColorLinkHover={config.brandColorLinkHover}
        />
      </FormProvider>
    );
  }

  // Show thank you page after successful submission
  if (showThankYou && config.thankYouPage) {
    const thankYouConfig = config.thankYouPage;
    const baseFormData = formData as unknown as BaseFormData;
    const businessNameForThankYou = (baseFormData.tradingName && typeof baseFormData.tradingName === 'string' ? baseFormData.tradingName : '') || 
                                   (baseFormData.businessName && typeof baseFormData.businessName === 'string' ? baseFormData.businessName : '') || 
                                   'your company';
    const processedConfig = {
      ...thankYouConfig,
      title: thankYouConfig.title,
      subtitle: thankYouConfig.subtitle,
      businessName: thankYouConfig.businessName?.replace('{{businessName}}', businessNameForThankYou),
      firstName: thankYouConfig.firstName?.replace('{{firstName}}', (formData as unknown as BaseFormData).firstName as string || ''),
      nextStepsItems: thankYouConfig.nextStepsItems,
      ctaButton: thankYouConfig.ctaButton,
      additionalInfo: thankYouConfig.additionalInfo,
      socialButton: thankYouConfig.socialButton
    };
    return (
      <FormProvider {...form}>
        <ThankYouPage 
          config={processedConfig} 
          className={className}
          brandColor={config.brandColor}
          brandColorHover={config.brandColorHover}
          brandColorText={config.brandColorText}
          brandColorLink={config.brandColorLink}
          brandColorLinkHover={config.brandColorLinkHover}
        />
      </FormProvider>
    );
  }

  return (
    <FormErrorBoundary
      onError={(error) => {
        handleError(error, 'MultiStepForm component');
        // Additional error reporting can be added here
      }}
    >
      <FormProvider {...form}>
        <form ref={formRef} onSubmit={(e) => e.preventDefault()} className={cn("w-full max-w-2xl flex flex-col flex-1 mx-auto md:px-4", className)}>
          {config.allowBackNavigation && (
            currentStep === 1 ? ( 
              <Button variant="outline" asChild grayBorder size="iconLg" onClick={goToPreviousStep} className="mb-4">
                <Link href={config.firstStepBackButtonLink || "/enquire"}>
                  <span className="material-symbols-outlined">
                    arrow_back
                  </span>
                </Link>
              </Button> 
            ) : (
              <Button variant="outline" grayBorder size="iconLg" onClick={goToPreviousStep} className="mb-4">
                <span className="material-symbols-outlined">arrow_back</span>
              </Button>
            )
          )}

          {/* Error Boundary Demo - Remove this in production 
          {process.env.NODE_ENV === 'development' && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-700 mb-2">FormErrorBoundary Demo (Development Only):</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShouldTriggerError(true);
                  }}
                  className="text-xs"
                >
                  Trigger Error Boundary
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleError(new Error('Test: Manual error reporting'), 'error boundary demo');
                  }}
                  className="text-xs"
                >
                  Test Error Handler
                </Button>
              </div>
            </div>
          )}*/}

          {/* Submit Error Display */}
          {submitError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex gap-2">
                <span className="material-symbols-outlined text-red-500">error</span>
                <div>
                  <p className="text-red-500 font-bold">Submission Error</p>
                  <p className="text-red-500">Having trouble? You can <Link href="/contact-us">contact support</Link> or try refreshing the page.</p>
                  {/* {submitAttempts >= 2 && (
                    <p className="text-red-700">
                      Having trouble? You can contact support or try refreshing the page.
                    </p>
                  )} */}
                </div>
              </div>
            </div>
          )}

          <AnimatePresence mode="wait" custom={animationDirection}>
            <motion.div
              key={`step-${currentStep}`}
              custom={animationDirection}
              data-step-content
              initial={{ 
                opacity: 0, 
                x: animationDirection === 'forward' ? 40 : -40,
                // scale: 0.98
              }}
              animate={{ 
                opacity: 1, 
                x: 0,
                scale: 1
              }}
              exit={{ 
                opacity: 0, 
                x: animationDirection === 'forward' ? -40 : 40,
                // scale: 0.98
              }}
              transition={{
                type: "tween",
                ease: [0.25, 0.1, 0.25, 1], // Custom easing for smoother feel
                duration: 0.4
              }}
              className="space-y-6"
            >
              <FormErrorBoundary fallback={
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">This form step encountered an error. Please try refreshing the page.</p>
                </div>
              }>
                <currentStepData.component {...stepProps} />
              </FormErrorBoundary>
            </motion.div>
          </AnimatePresence>

          {/* reCAPTCHA v3 Status */}
          {config.brandColor && config.brandColorHover && config.brandColorText && (
            <style dangerouslySetInnerHTML={{
              __html: `
                .button-branded {
                  background-color: ${config.brandColor} !important;
                  color: ${config.brandColorText} !important;
                }

                .button-branded:hover {
                  background-color: ${config.brandColorHover} !important;
                }
              `
            }} />
          )}
          {config.brandColorLink && config.brandColorLinkHover && (
            <style dangerouslySetInnerHTML={{
              __html: `
                .text-branded {
                  color: ${config.brandColorLink} !important;
                }
                p a {
                  color: ${config.brandColorLink} !important;
                }
                p a:hover {
                  color: ${config.brandColorLinkHover} !important;
                }
              `
            }} />
          )}
          {/* Centralized Submit Button - Hide only on last step when hideSubmitButton is true */}
          {!(config.hideSubmitButton && isLastStep) && (
            <div className="mt-12 flex flex-col gap-2">
              <Button
                onClick={handleFormSubmit}
                variant="primary"
                size="lg"
                disabled={
                  isSubmitting || 
                  !isCurrentStepValid ||
                  (recaptchaConfig.enabled && isLastStep && !recaptchaReady)
                }
                className={cn(
                  submitButtonConfig.className,
                  config.brandColor && config.brandColorHover && config.brandColorText ? 'button-branded' : ''
                )}
              >
                {isSubmitting ? (
                  <>
                    <span className="material-symbols-outlined animate-spin mr-2">progress_activity</span>
                    {submitButtonConfig.loadingText || 'Submitting...'}
                  </>
                ) : (
                  (() => {
                    // Check for custom button text on current step
                    const customButtonText = currentStepData.nextButtonText;
                    if (customButtonText) {
                      // If it's a function, call it with reactive form data
                      return typeof customButtonText === 'function' 
                        ? customButtonText(formData)
                        : customButtonText;
                    }
                    // Default button text
                    return isLastStep ? (submitButtonConfig.text || 'Submit') : isFirstStep ? 'Get Started': 'Next';
                  })()
                )}
              </Button>
              
              {/* Helper text for submit button */}
              {(() => {
                // Check for custom helper text on current step
                if (currentStepData.helperText) {
                  return (
                    <p className="paragraph-sm text-muted-foreground text-center">
                      {currentStepData.helperText}
                    </p>
                  );
                }
                // Use configurable helper text with defaults
                if (isFirstStep) {
                  const firstStepHelperText = config.helperText?.firstStep || 'Takes only a few minutes';
                  return (
                    <p className="paragraph-sm text-muted-foreground text-center">
                      {firstStepHelperText}
                    </p>
                  );
                }
                if (isLastStep) {
                  const lastStepHelperText = config.helperText?.lastStep || 'Our team will get back to you within 1 business day';
                  return (
                    <p className="paragraph-sm text-muted-foreground text-center">
                      {lastStepHelperText}
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          )}

          {config.showProgress && !(config.hideSubmitButton && isLastStep) && (
            <div className="mt-12 max-w-xs mx-auto w-[240px] md:w-[360px]">
              <div className="w-full bg-gray-300 rounded-full h-1">
                <div className="bg-gray-950 h-1 rounded-full transition-all duration-300" style={{ width: `${progressPercentage}%` }} />
              </div>
            </div>
          )}
        </form>
      </FormProvider>
    </FormErrorBoundary>
  );
}

// Loading fallback component
const FormLoadingFallback = () => (
  <div className="flex items-center justify-center p-8">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading form...</p>
    </div>
  </div>
);

// Wrapper component that provides reCAPTCHA v3 context only when needed
export function MultiStepForm<T extends object>(props: MultiStepFormProps<T>) {
  // Only wrap with reCAPTCHA provider if the form config enables it
  if (props.config.requireRecaptcha !== undefined && props.config.requireRecaptcha !== false) {
    return (
      <Suspense fallback={<FormLoadingFallback />}>
        <ReCaptchaV3Provider>
          <MultiStepFormInner {...props} />
        </ReCaptchaV3Provider>
      </Suspense>
    );
  }

  // For forms without reCAPTCHA, render directly with Suspense
  return (
    <Suspense fallback={<FormLoadingFallback />}>
      <MultiStepFormInner {...props} />
    </Suspense>
  );
} 